import { useEffect, useState } from "react";
import { toast } from "sonner";
import { IdCard, Loader2, ShieldCheck, User } from "lucide-react";
import { usePartnerAuth, formatApiError } from "@/context/PartnerAuthContext";

/**
 * LegalNameBackfillModal — fleet-wide one-time prompt.
 *
 * Shown on next login for any Armada SSO account created BEFORE the two-field
 * name standard shipped (2026-02-03). Collects the partner's Legal Name
 * (required) + optional Display Name update. Once saved, this modal never
 * reappears for that account.
 *
 * The modal is non-dismissible by default — the account cannot proceed to
 * dashboard functions until Legal Name is on file (because agreements auto-
 * populate from it).
 *
 * Mount near the top of PartnerDashboard. If `partner.legal_name_pending` is
 * false the modal returns null.
 */
export default function LegalNameBackfillModal() {
    const { partner, setPartnerNames } = usePartnerAuth();
    const [legal, setLegal] = useState("");
    const [display, setDisplay] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (partner?.display_name && !display) setDisplay(partner.display_name);
    }, [partner, display]);

    if (!partner || !partner.legal_name_pending) return null;

    const submit = async (e) => {
        e?.preventDefault();
        const l = legal.trim();
        if (l.length < 2) {
            toast.error("Please enter your legal name as it appears on your ID.");
            return;
        }
        setSaving(true);
        try {
            await setPartnerNames(l, display.trim() || undefined);
            toast.success("Legal Name on file — thanks");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            data-testid="legal-name-backfill-modal"
        >
            <div className="bg-cream max-w-md w-full rounded-sm shadow-2xl overflow-hidden">
                <header className="px-6 pt-6 pb-3">
                    <div className="inline-flex items-center gap-2 text-gold text-[0.6rem] tracking-[0.3em] uppercase font-semibold">
                        <ShieldCheck size={12} /> Armada SSO · One-Time Confirmation
                    </div>
                    <h3 className="font-serif text-xl text-forest mt-2 leading-tight">
                        Confirm your Legal Name
                    </h3>
                    <p className="text-xs text-brown mt-2 leading-relaxed">
                        Your Legal Name auto-populates every agreement and
                        compliance document we send on your behalf. It is{" "}
                        <strong className="text-forest">
                            never shown publicly
                        </strong>{" "}
                        — only your Display Name appears on your profile.
                    </p>
                </header>

                <form onSubmit={submit} className="px-6 pb-5 space-y-4">
                    <label className="block">
                        <span className="text-[0.6rem] tracking-[0.25em] uppercase text-brown inline-flex items-center gap-1.5">
                            <IdCard size={11} /> Legal Name
                        </span>
                        <input
                            type="text"
                            required
                            autoComplete="name"
                            value={legal}
                            onChange={(e) => setLegal(e.target.value)}
                            placeholder="First Last"
                            data-testid="legal-name-input"
                            className="mt-1.5 w-full px-3 py-2 border border-forest/20 rounded-sm text-sm text-forest focus:outline-none focus:border-gold"
                            autoFocus
                        />
                        <span className="text-[0.6rem] text-brown mt-1 block">
                            As it appears on your government-issued ID. Not a
                            brand name or handle.
                        </span>
                    </label>
                    <label className="block">
                        <span className="text-[0.6rem] tracking-[0.25em] uppercase text-brown inline-flex items-center gap-1.5">
                            <User size={11} /> Display Name
                            <span className="text-brown/60 normal-case tracking-normal">
                                (optional)
                            </span>
                        </span>
                        <input
                            type="text"
                            autoComplete="nickname"
                            value={display}
                            onChange={(e) => setDisplay(e.target.value)}
                            placeholder="What shows publicly — defaults to first name"
                            data-testid="display-name-input"
                            className="mt-1.5 w-full px-3 py-2 border border-forest/20 rounded-sm text-sm text-forest focus:outline-none focus:border-gold"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="legal-name-submit"
                        className="btn-pill btn-gold w-full justify-center text-xs disabled:opacity-40"
                    >
                        {saving ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <>Save &amp; Continue</>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
